import React from 'react';
import { NavLink } from 'react-router-dom';
import { CreditCard, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Money } from '@/components/common/Money';

interface DashboardPayoutCardProps {
  availableBalanceMinor: number;
  minimumPayoutMinor: number;
  payoutMethod: {
    type: 'bkash' | 'nagad' | 'bank';
    accountDetails: string;
  } | null;
}

export const DashboardPayoutCard: React.FC<DashboardPayoutCardProps> = ({
  availableBalanceMinor,
  minimumPayoutMinor,
  payoutMethod,
}) => {
  const isEligible = availableBalanceMinor >= minimumPayoutMinor;

  return (
    <div className="bg-[#0E1015] border border-zinc-800/80 rounded-2xl p-6">
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <CreditCard className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Payout Disbursements</h2>
            <p className="text-xs text-zinc-400">
              Receive your earnings directly into local Bangladeshi accounts
            </p>
          </div>
        </div>
        <span className="text-xs font-mono text-zinc-400">
          Threshold: <Money amountMinor={minimumPayoutMinor} />
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-col justify-between">
          <div>
            <span className="text-zinc-500 uppercase tracking-wider text-[10px] font-semibold block mb-1">
              Active Disbursement Channel
            </span>
            {payoutMethod ? (
              <div className="flex items-center gap-2 text-white font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="capitalize">{payoutMethod.type}: </span>
                <span className="font-mono text-zinc-300">{payoutMethod.accountDetails}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-zinc-400">
                <AlertCircle className="w-4 h-4 text-amber-400" />
                <span>No payout account configured yet</span>
              </div>
            )}
          </div>
          <NavLink
            to="/payouts"
            className="mt-4 text-indigo-400 hover:text-indigo-300 font-medium inline-flex items-center gap-1 transition"
          >
            <span>{payoutMethod ? 'Manage payout settings' : 'Set up bKash or Bank details'}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </NavLink>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-col justify-between">
          <div>
            <span className="text-zinc-500 uppercase tracking-wider text-[10px] font-semibold block mb-1">
              Withdrawal Status
            </span>
            <p className="text-white font-semibold text-sm">
              <Money amountMinor={availableBalanceMinor} />
            </p>
            <p className="text-zinc-400 mt-1 leading-snug">
              {isEligible
                ? 'Your balance meets the withdrawal threshold. Payouts are reviewed within 24 hours.'
                : 'Accumulate at least ৳1,000 in approved commissions to request a withdrawal.'}
            </p>
          </div>

          <NavLink
            to="/payouts"
            className={`mt-4 px-3 py-2 rounded-lg text-center font-medium transition inline-block ${
              isEligible
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-xs'
                : 'bg-zinc-800/60 text-zinc-500 cursor-not-allowed'
            }`}
          >
            <span>Request Payout</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
};
